import React, {FC, useEffect, useState} from "react";
import {AdditionalServiceDto} from "../../types/contracts";
import {FieldErrors, FieldValues, UseFormRegister} from "react-hook-form";
import {IAdditionalServicesDataType} from "../../types/IFormData";
import {TALK_AND_FOLKLOR_DATE, TRIP_DATE} from "../../constants";
import {format} from "date-fns";

interface Props {
    register: UseFormRegister<FieldValues>;
    errors: FieldErrors;
    unregister: (name: string) => void;
    additionalServices: AdditionalServiceDto[];
    selectedDates: string[] | null;
    additionalServicesData: IAdditionalServicesDataType;
}

const AdditionalServicesFormComponent: FC<Props> = ({register, errors, unregister, additionalServices, selectedDates, additionalServicesData}) => {
    const [filteredServices, setFilteredServices] = useState<AdditionalServiceDto[]>([]);
    const [showProgramNote, setShowProgramNote] = useState(false);
    const [showTripNote, setShowTripNote] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        try {
            if (!Array.isArray(additionalServices)) throw new Error("Invalid services");

            const filtered = selectedDates
                ? additionalServices.filter(service => !service.date || selectedDates.includes(service.date))
                : additionalServices;

            setFilteredServices(prev => {
                const removed = prev.filter(prevService => !filtered.some(s => s.id === prevService.id));
                if (removed.length) unregister("additionalServices");
                return filtered;
            });

            const includesDate = (targetDate: string) =>
                filtered.some(
                    service => service.date && format(service.date, "dd.MM.yyyy") === targetDate
                );

            setShowProgramNote(selectedDates ? includesDate(TALK_AND_FOLKLOR_DATE) : true);
            setShowTripNote(selectedDates ? includesDate(TRIP_DATE) : true);
        } catch (err) {
            console.error("Error processing services:", err);
            setHasError(true);
        }
    }, [selectedDates, additionalServices]);

    if (hasError) {
        return (
            <div className="text-red-600 font-medium">
                Došlo k chybě při načítání doplňkových služeb. Zkuste to prosím později.
            </div>
        );
    }

    return (
        <>
            <h2 className="text-base lg:text-lg font-semibold mb-2">
                {additionalServicesData.label}
            </h2>

            {filteredServices.length === 0 ? (
                <p className="text-gray-700 mb-4">Žádné doplňkové služby.</p>
            ) : (
                <>
                    {filteredServices
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(service => (
                            <div key={service.id} className="flex items-center">
                                <input
                                    type="checkbox"
                                    id={service.id}
                                    value={service.id}
                                    className="mr-2"
                                    {...register(additionalServicesData.name, {
                                        required: additionalServicesData.requiredErrorMessage,
                                    })}
                                />
                                <label htmlFor={service.id}>
                                    {service.name} ({service.amount} Kč)
                                </label>
                            </div>
                        ))}

                    {errors[additionalServicesData.name]?.message && (
                        <span className="text-primary-dark text-sm font-medium">
                            {errors[additionalServicesData.name]?.message as string}
                        </span>
                    )}

                    {showProgramNote && (
                        <p className="mt-2">* Vyberte pouze 1 z nabízených variant programů.</p>
                    )}
                    {showTripNote && (
                        <p>** Vyberte pouze 1 z nabízených variant zájezdů.</p>
                    )}
                </>
            )}
        </>
    );
};

export default AdditionalServicesFormComponent;
